import { Icon, Alert, Button } from '@mgmresorts/mgm-ui';
import { navigate } from 'hookrouter';
import React, { useCallback, useMemo, useState, useEffect } from 'react';

import { ActivateAccountMessage } from '../../../components/ui/ActivateAccountMessage';
import { Container } from '../../../components/ui/Container';
import { PageLayout } from '../../../components/ui/PageLayout';
import { WelcomeMGMRewards } from '../../../components/ui/WelcomeMGMRewards';
import { usePasskeyEnabled } from '../../../hooks';
import { useAppRedirect } from '../../../hooks/useAppRedirect';
import { useContent } from '../../../hooks/useContent';
import { routes } from '../../../routes';
import { useEvent } from '../../../utils/analytics/useEvent';
import { usePageLoadEvent } from '../../../utils/analytics/usePageLoadEvent';
import { useAuthContext } from '../../../utils/auth/AuthProvider';
import { IDENTITY_WIDGET_PATHS, IDW_FLOW_PARAM } from '../../../utils/constants';
import { getPersistedParameters } from '../../../utils/persistParameterContext';
import { useUserAccount } from '../../../utils/userAccountContext';
import { ConfirmModal } from '../ConfirmModal';

import {
  StyledVerificationText,
  StyledDescriptionText,
  StyledEmailFieldLabel,
  StyledEnrollmentSection,
  StyledEnrollmentButtonGroup,
  StyledFooterLinks,
  StyledLinkButton,
  StyledSkipButton,
  StyledDivider,
  StyledDivContainer,
} from './styles';
import { MfaEnrollmentFlowType, MFA_ENROLLMENT_FLOW_CONFIG } from './types';

export function MFAEnrollmentPage({ enrollmentFlow }: { enrollmentFlow?: MfaEnrollmentFlowType }) {
  const redirectToApp = useAppRedirect();
  const params = getPersistedParameters();
  const { getContentByPath } = useContent();
  const trackEvent = useEvent();
  const [isPasskeySupported, setIsPasskeySupported] = useState<boolean | null>(null);
  const [openAlert, setOpenAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showWelcomeMgmRewardsScreen, setShowWelcomeMgmRewardsScreen] = useState(false);
  const [showAccountActivatedScreen, setShowAccountActivatedScreen] = useState(false);
  const { authClient } = useAuthContext();
  // Get flow from persisted parameters
  const idwFlow = params?.[IDW_FLOW_PARAM] as MfaEnrollmentFlowType | undefined;
  const currentFlow = enrollmentFlow || idwFlow;

  // Generic modal state (can be used for success/error messages)
  const [showPasskeyErrorModal, setShowPasskeyErrorModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const [modalCancelLabel, setModalCancelLabel] = useState<string>('');
  const { mgmId } = useUserAccount();
  const { passkeyEnabled } = usePasskeyEnabled();
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);

  // Get verification config based on query parameter
  const verificationConfig = useMemo(() => {
    if (currentFlow && currentFlow in MFA_ENROLLMENT_FLOW_CONFIG) {
      return MFA_ENROLLMENT_FLOW_CONFIG[currentFlow];
    }
    // Default fallback to current behavior
    return {
      message: 'Your email is verified',
    };
  }, [currentFlow]);

  // Dynamic page name based on verification type
  const pageName = useMemo(() => {
    if (currentFlow === 'activate') {
      return 'active account mfa setup';
    } else if (currentFlow === 'passkey') {
      return 'passkey added 2fa setup';
    }
    return 'sign up mfa setup';
  }, [currentFlow]);

  // Use dynamic page load event
  usePageLoadEvent('identity', pageName);

  useEffect(() => {
    const check = async () => {
      const isSupported =
        window.PublicKeyCredential && (await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable());

      setIsPasskeySupported(!!isSupported);
    };

    check();
  }, []);

  const handleEnrollWithEmail = useCallback(async () => {
    await trackEvent('identity – unified MFA CTAs', 'Enroll with email', 'unifiedmfactas', {}, true);

    const targetPath = IDENTITY_WIDGET_PATHS.MfaEnrollEmail;
    if (routes[targetPath]) {
      navigate(targetPath, true, { [IDW_FLOW_PARAM]: idwFlow });
    } else {
      setErrorMessage(getContentByPath('MFA.enrollment.systemError'));
      setOpenAlert(true);
    }
  }, [trackEvent, getContentByPath, idwFlow]);

  const handleEnrollWithSMS = useCallback(async () => {
    await trackEvent('identity – unified MFA CTAs', 'Enroll with text message (SMS)', 'unifiedmfactas', {}, true);
    const targetPath = IDENTITY_WIDGET_PATHS.MfaEnrollSms;
    if (routes[targetPath]) {
      navigate(targetPath, true, { [IDW_FLOW_PARAM]: idwFlow });
    } else {
      setErrorMessage(getContentByPath('MFA.enrollment.systemError'));
      setOpenAlert(true);
    }
  }, [trackEvent, getContentByPath, idwFlow]);

  const handleEnrollWithPasskey = useCallback(async () => {
    setIsPasskeyLoading(true);
    await trackEvent('identity – unified MFA CTAs', 'Enroll with passkey', 'unifiedmfactas', {}, true);
    try {
      // Check if passkey feature flag is enabled
      if (!passkeyEnabled) {
        throw new Error('Passkey enrollment is currently disabled.');
      }

      // Get mgmId from context or fetch from access token if not available
      let userMgmId = mgmId;

      const accessToken = await authClient?.getAccessToken();
      if (accessToken?.claims?.uid) {
        userMgmId = String(accessToken.claims.uid);
        console.log('Retrieved mgmId from access token:', userMgmId);
      } else {
        throw new Error('Unable to retrieve mgmId from access token');
      }

      const mfaManager = await authClient?.mfa(userMgmId);
      const enrollResult = await mfaManager?.enrollPasskeyAuthenticator();

      if (enrollResult?.success && enrollResult?.data) {
        console.log('✅ Passkey enrollment successful');
        // Activate passkey (triggers WebAuthn ceremony)
        const activationResult = await mfaManager?.activatePasskeyAuthenticator(
          enrollResult.data.id,
          enrollResult.data,
        );

        if (activationResult?.success) {
          console.log('✅ Passkey MFA activated successfully');
          await trackEvent(
            'identity – unified MFA notification',
            'Passkey authentication has been added',
            'unifiedmfanotification',
            { notificationDisplayed: 'Passkey authentication has been added' },
            true,
          );

          navigate(IDENTITY_WIDGET_PATHS.MfaEnrollPasskey, true, { [IDW_FLOW_PARAM]: idwFlow });
        } else {
          throw new Error('Passkey activation failed');
        }
      } else {
        console.error('Passkey enrollment failed:', enrollResult?.error);
        throw enrollResult?.error;
      }
    } catch (error) {
      setModalCancelLabel(getContentByPath('MFA.confirmModal.tryAgain'));
      setModalTitle(getContentByPath('MFA.confirmModal.incompletePasskeyTitle'));
      setModalMessage(getContentByPath('MFA.confirmModal.incompletePasskeyDescription'));
      setShowPasskeyErrorModal(true);
      console.error('Passkey MFA error:', error);
    } finally {
      setIsPasskeyLoading(false);
    }
  }, [authClient, mgmId, trackEvent, getContentByPath, idwFlow, passkeyEnabled]);

  const handleViewFAQs = useCallback(async () => {
    await trackEvent('identity – unified MFA CTAs', 'View FAQs', 'unifiedmfactas', {}, true);
    window.open(getContentByPath('MFA.enrollment.faqUrl'), '_blank');
  }, [getContentByPath, trackEvent]);

  const handlePrivacyPolicy = useCallback(async () => {
    await trackEvent('identity – unified MFA CTAs', 'Privacy policy', 'unifiedmfactas', {}, true);
    window.open(getContentByPath('MFA.enrollment.privacyPolicyUrl'), '_blank');
  }, [getContentByPath, trackEvent]);

  const handleSkipForNow = useCallback(async () => {
    await trackEvent('identity – unified MFA CTAs', 'Skip for now', 'unifiedmfactas', {}, true);
    if (currentFlow === 'passkey') {
      // Show modal without tracking modal display - only track CTA button clicks
      setModalCancelLabel(getContentByPath('MFA.confirmModal.addMethod'));
      setModalTitle(getContentByPath('MFA.confirmModal.skipEnrollmentTile'));
      setModalMessage(getContentByPath('MFA.confirmModal.skipEnrollmentDescription'));
      setShowPasskeyErrorModal(true);
      return;
    } else if (currentFlow === 'activate') {
      setShowAccountActivatedScreen(true);
    } else {
      setShowWelcomeMgmRewardsScreen(true);
    }
    // Add 2-second delay before redirecting to app
    setTimeout(() => {
      redirectToApp();
    }, 2000);
  }, [trackEvent, currentFlow, redirectToApp, getContentByPath]);

  const onCloseAlert = () => {
    setOpenAlert(false);
    setErrorMessage('');
  };
  // Generic confirm modal close handler
  const handleModalClose = useCallback(async () => {
    // Track only when user clicks "Add Method" button in the "Skip two-factor authentication?" modal
    if (modalCancelLabel === getContentByPath('MFA.confirmModal.addMethod')) {
      await trackEvent(
        'identity – unified MFA CTAs',
        'Add Method - Skip two-factor authentication?',
        'unifiedmfactas',
        {},
        true,
      );
    }

    setShowPasskeyErrorModal(false);
    setModalTitle('');
    setModalMessage('');
  }, [trackEvent, modalCancelLabel, getContentByPath]);

  // Generic confirm modal skip for now handler
  const handleSkipEnrollment = useCallback(async () => {
    // Track when user clicks "Skip for now" button in the "Skip two-factor authentication?" modal
    await trackEvent(
      'identity – unified MFA CTAs',
      'Skip for now - Skip two-factor authentication?',
      'unifiedmfactas',
      {},
      true,
    );
    if (currentFlow === 'activate') {
      setShowAccountActivatedScreen(true);
    } else {
      setShowWelcomeMgmRewardsScreen(true);
    }
    setShowPasskeyErrorModal(false);
    setModalTitle('');
    setModalMessage('');
    // Add 2-second delay before redirecting to app
    setTimeout(() => {
      redirectToApp();
    }, 2000);
  }, [redirectToApp, trackEvent, currentFlow]);

  // if (!mfaEnabled) {
  //   // TODO: Redirect to sign-in page
  //   return null;
  // }

  return (
    <PageLayout
      title={getContentByPath('MFA.title')}
      testId={`route-/mfa/enroll${enrollmentFlow ? `/${enrollmentFlow}` : ''}`}
      description='Set up multi-factor authentication for enhanced security'
    >
      {showAccountActivatedScreen ? (
        <ActivateAccountMessage />
      ) : showWelcomeMgmRewardsScreen ? (
        <WelcomeMGMRewards />
      ) : (
        <Container height='auto' width='491px'>
          <Icon name='checkmark-circle' variant='outlined' size='large' color='#007636' />

          <StyledVerificationText variant='heading-medium-3'>{verificationConfig.message}</StyledVerificationText>

          <StyledDescriptionText variant='body-regular-s'>
            {getContentByPath('MFA.enrollment.description')}
          </StyledDescriptionText>
          {errorMessage && (
            <Alert
              open={openAlert}
              onClose={onCloseAlert}
              variant='error'
              message={errorMessage}
              data-testid='code-resent-error'
              role='alert'
            />
          )}
          <StyledDivContainer>
            <StyledEnrollmentSection>
              <StyledEmailFieldLabel variant='body-regular-m'>
                {getContentByPath('MFA.enrollment.twoFactor')}
              </StyledEmailFieldLabel>

              <StyledEnrollmentButtonGroup>
                {!verificationConfig.hideEmailEnrollment && (
                  <Button
                    variant='primary'
                    size='large'
                    onClick={handleEnrollWithEmail}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleEnrollWithEmail();
                      }
                    }}
                    testId='enroll-email'
                    type='button'
                    label={getContentByPath('MFA.enrollment.enrollWithEmail')}
                    shape='square'
                    fullWidth
                  />
                )}

                {!verificationConfig.hideSMSEnrollment && (
                  <Button
                    variant='secondary'
                    size='large'
                    onClick={handleEnrollWithSMS}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleEnrollWithSMS();
                      }
                    }}
                    testId='enroll-sms'
                    type='button'
                    label={getContentByPath('MFA.enrollment.enrollWithSMS')}
                    shape='square'
                    fullWidth
                  />
                )}
              </StyledEnrollmentButtonGroup>
            </StyledEnrollmentSection>
            {passkeyEnabled && !verificationConfig.hidePasskeyEnrollment && isPasskeySupported && (
              <>
                <StyledDivider direction='horizontal' inverted={false} />
                <StyledEmailFieldLabel variant='body-regular-m'>
                  {getContentByPath('MFA.enrollment.enrollPasskey')}
                </StyledEmailFieldLabel>
                <Button
                  variant='secondary'
                  size='large'
                  onClick={handleEnrollWithPasskey}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleEnrollWithPasskey();
                    }
                  }}
                  testId='enroll-passkey'
                  type='button'
                  label={getContentByPath('MFA.enrollment.enrollWithPasskey')}
                  shape='square'
                  fullWidth
                  disabled={!isPasskeySupported}
                  loading={isPasskeyLoading}
                />
              </>
            )}
          </StyledDivContainer>
          <StyledFooterLinks>
            <StyledLinkButton
              onClick={handleViewFAQs}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleViewFAQs();
                }
              }}
              data-testid='view-faqs'
              type='button'
            >
              {getContentByPath('MFA.enrollment.faq')}
              <Icon
                name='arrow-right'
                variant='outlined'
                size='small'
                color='#00012B'
                width={14}
                height={14}
                style={{ marginLeft: '4px' }}
              />
            </StyledLinkButton>

            <StyledLinkButton
              onClick={handlePrivacyPolicy}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handlePrivacyPolicy();
                }
              }}
              data-testid='privacy-policy'
              type='button'
            >
              {getContentByPath('MFA.enrollment.privacyPolicy')}
            </StyledLinkButton>
          </StyledFooterLinks>
          <StyledSkipButton
            variant='tertiary'
            size='large'
            onClick={handleSkipForNow}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSkipForNow();
              }
            }}
            data-testid='skip-for-now'
            type='button'
          >
            {getContentByPath('MFA.enrollment.skipForNow')}
          </StyledSkipButton>
          <ConfirmModal
            isOpen={showPasskeyErrorModal}
            title={modalTitle}
            message={modalMessage}
            cancelLabel={modalCancelLabel}
            confirmLabel={getContentByPath('MFA.enrollment.skipForNow')}
            onClose={handleModalClose}
            onConfirm={handleSkipEnrollment}
            showCloseButton={true}
            testId='passkey-modal'
          />
        </Container>
      )}
    </PageLayout>
  );
}
